import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { ArrowLeft, User, Mail, Phone, MapPin, Calendar, DollarSign, FileText, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface Traveler {
  id: number;
  application_id: number;
  first_name: string;
  last_name: string;
  passport_number: string;
  [key: string]: any; // Allow any additional fields from database
}

interface VipTransfer {
  id: number;
  application_id: number;
  vehicle_type_id: number;
  vehicle_type_name?: string;
  vehicle_price?: number;
  price_per_km?: number;
  max_passengers?: number;
  max_luggage?: number;
  pickup_location?: string;
  dropoff_location?: string;
}

interface OrderDetails {
  ID: number;
  post_status: string;
  post_date: string;
  order_total: string;
  payment_method?: string;
}

interface OrderItem {
  id: number;
  name: string;
  type: string;
  meta: Record<string, string>;
}

interface Calculations {
  visaFeeTotal?: number;
  planFeeTotal?: number;
  vipFeeTotal?: number;
  totalAmount?: number;
}

const ApplicationDetail = () => {
  const { id } = useParams<{ id: string }>();

  const { data: applicationData, isLoading, error } = useQuery(
    ['application', id],
    async () => {
      const response = await api.get(`/applications/${id}`);
      return response.data;
    }
  );

  // Helper function to format dates safely
  const formatDate = (dateString: string | null | undefined, formatType: 'date' | 'datetime' = 'date') => {
    if (!dateString) return 'Belirtilmemiş';
    
    try {
      // Handle ISO date strings like "1990-03-31T21:00:00.000Z"
      let date: Date;
      
      if (typeof dateString === 'string' && dateString.includes('T')) {
        // ISO format - parse directly, it handles timezone correctly
        date = new Date(dateString);
      } else {
        date = new Date(dateString);
      }
      
      if (isNaN(date.getTime())) return 'Geçersiz Tarih';
      
      if (formatType === 'datetime') {
        return format(date, 'dd MMMM yyyy HH:mm', { locale: tr });
      } else {
        // For dates like "1990-03-31T21:00:00.000Z", show as "31 Mart 1990"
        return format(date, 'dd MMMM yyyy', { locale: tr });
      }
    } catch (error) {
      return 'Geçersiz Tarih';
    }
  };

  const application = applicationData?.application;
  const travelers: Traveler[] = applicationData?.travelers || [];
  const vipTransfers: VipTransfer[] = applicationData?.vipTransfers || [];
  const orderDetails: OrderDetails | null = applicationData?.orderDetails || null;
  const orderItems: OrderItem[] = applicationData?.orderItems || [];
  const calculations: Calculations | null = applicationData?.calculations || null;

  // Debug: Log traveler fields to see what's coming from backend
  if (travelers.length > 0) {
    console.log('Traveler fields:', Object.keys(travelers[0]));
    console.log('First traveler data:', travelers[0]);
    
    // Log supporting document fields specifically
    const firstTraveler = travelers[0];
    console.log('Supporting document fields:');
    console.log('- supporting_document_id:', firstTraveler.supporting_document_id);
    console.log('- supporting_doc_type:', firstTraveler.supporting_doc_type);
    console.log('- supporting_doc_number:', firstTraveler.supporting_doc_number);
    console.log('- supporting_doc_start_date:', firstTraveler.supporting_doc_start_date);
    console.log('- supporting_doc_expiry_date:', firstTraveler.supporting_doc_expiry_date);
    console.log('- supporting_doc_expiry_unlimited:', firstTraveler.supporting_doc_expiry_unlimited);
  }

  const getStatusIcon = (status: string) => {
    if (!status) return <AlertCircle className="h-5 w-5 text-gray-500" />;
    
    switch (status.toLowerCase()) {
      case 'approved':
      case 'paid':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'unpaid':
        return <AlertCircle className="h-5 w-5 text-blue-500" />;
      case 'rejected':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'approved':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'processing':
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'unpaid':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    if (!status) return 'Bilinmiyor';
    
    switch (status.toLowerCase()) {
      case 'approved':
        return 'Onaylandı';
      case 'paid':
        return 'Ödendi';
      case 'processing':
        return 'İşlemde';
      case 'pending':
        return 'Beklemede';
      case 'unpaid':
        return 'Ödenmedi';
      case 'rejected':
        return 'Reddedildi';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Başvuru detayları yüklenirken bir hata oluştu.</p>
      </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/applications"
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Geri Dön
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Başvuru #{application.id}
              </h1>
                             <p className="text-sm text-gray-500">
                 {formatDate(application.created_at, 'datetime')}
               </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusIcon(application.application_status)}
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application.application_status)}`}>
              {getStatusText(application.application_status)}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Details */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Müşteri Bilgileri
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {application.reference_id}
                      </p>
                      <p className="text-sm text-gray-500">Referans ID</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {application.country_name}
                      </p>
                      <p className="text-sm text-gray-500">Ülke</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {application.plan}
                      </p>
                      <p className="text-sm text-gray-500">Plan</p>
                    </div>
                  </div>
                  
                  {application.arrival_date && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                                                 <p className="text-sm font-medium text-gray-900">
                           {formatDate(application.arrival_date)}
                         </p>
                        <p className="text-sm text-gray-500">Varış Tarihi</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Application Details */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Başvuru Detayları
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {travelers.length}
                      </p>
                      <p className="text-sm text-gray-500">Yolcu Sayısı</p>
                    </div>
                  </div>
                  
                  {vipTransfers.length > 0 && (
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          VIP Transfer Dahil
                        </p>
                        <p className="text-sm text-gray-500">Transfer Hizmeti</p>
                      </div>
                    </div>
                  )}
                  
                  {application.entry_type && (
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {application.entry_type}
                        </p>
                        <p className="text-sm text-gray-500">Giriş Türü</p>
                      </div>
                    </div>
                  )}
                  
                  {application.visa_validity_days && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {application.visa_validity_days} gün
                        </p>
                        <p className="text-sm text-gray-500">Vize Geçerlilik</p>
                      </div>
                    </div>
                  )}
                  
                  {application.visa_duration_days && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {application.visa_duration_days} gün
                        </p>
                        <p className="text-sm text-gray-500">Vize Süresi</p>
                      </div>
                    </div>
                  )}
                  
                  {application.protection !== null && application.protection !== undefined && (
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {application.protection ? 'Evet' : 'Hayır'}
                        </p>
                        <p className="text-sm text-gray-500">Denial Protection</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Travelers */}
            {travelers.length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Yolcu Bilgileri ({travelers.length})
                  </h3>
                  <div className="space-y-4">
                                         {travelers.map((traveler, index) => (
                       <div key={index} className="border border-gray-200 rounded-lg p-4">
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div>
                             <p className="text-sm font-medium text-gray-900">
                               {traveler.first_name} {traveler.last_name}
                             </p>
                             <p className="text-sm text-gray-500">Ad Soyad</p>
                           </div>
                           <div>
                             <p className="text-sm font-medium text-gray-900">
                               {traveler.passport_number}
                             </p>
                             <p className="text-sm text-gray-500">Pasaport No</p>
                           </div>
                           {traveler.email && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {traveler.email}
                               </p>
                               <p className="text-sm text-gray-500">E-posta</p>
                             </div>
                           )}
                           {traveler.phone && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {traveler.phone}
                               </p>
                               <p className="text-sm text-gray-500">Telefon</p>
                             </div>
                           )}
                           {traveler.gender && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {traveler.gender}
                               </p>
                               <p className="text-sm text-gray-500">Cinsiyet</p>
                             </div>
                           )}
                           {(traveler.date_of_birth || traveler.dob) && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {formatDate(traveler.date_of_birth || traveler.dob)}
                               </p>
                               <p className="text-sm text-gray-500">Doğum Tarihi</p>
                             </div>
                           )}
                           {traveler.nationality && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {traveler.nationality}
                               </p>
                               <p className="text-sm text-gray-500">Uyruk</p>
                             </div>
                           )}
                           {traveler.passport_expiry && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {formatDate(traveler.passport_expiry)}
                               </p>
                               <p className="text-sm text-gray-500">Pasaport Geçerlilik</p>
                             </div>
                           )}
                           {traveler.passport_issue_date && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {formatDate(traveler.passport_issue_date)}
                               </p>
                               <p className="text-sm text-gray-500">Pasaport Veriliş Tarihi</p>
                             </div>
                           )}
                           {traveler.passport_issue_country && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {traveler.passport_issue_country}
                               </p>
                               <p className="text-sm text-gray-500">Pasaport Veriliş Ülkesi</p>
                             </div>
                           )}
                           {traveler.created_at && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {formatDate(traveler.created_at, 'datetime')}
                               </p>
                               <p className="text-sm text-gray-500">Kayıt Tarihi</p>
                             </div>
                           )}
                           {traveler.updated_at && (
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {formatDate(traveler.updated_at, 'datetime')}
                               </p>
                               <p className="text-sm text-gray-500">Güncelleme Tarihi</p>
                             </div>
                           )}
                                                                                   {/* Passport Type */}
                            {traveler.passport_type_id && (
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {traveler.passport_type_id}
                                </p>
                                <p className="text-sm text-gray-500">Pasaport Türü</p>
                              </div>
                            )}
                            
                            {/* Place of Birth */}
                            {traveler.place_of_birth && (
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {traveler.place_of_birth}
                                </p>
                                <p className="text-sm text-gray-500">Doğum Yeri</p>
                              </div>
                            )}
                            
                            {/* Mother Name */}
                            {traveler.mother_name && (
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {traveler.mother_name}
                                </p>
                                <p className="text-sm text-gray-500">Anne Adı</p>
                              </div>
                            )}
                            
                            {/* Father Name */}
                            {traveler.father_name && (
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {traveler.father_name}
                                </p>
                                <p className="text-sm text-gray-500">Baba Adı</p>
                              </div>
                            )}
                            
                            {/* Address */}
                            {traveler.address && (
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {traveler.address}
                                </p>
                                <p className="text-sm text-gray-500">Adres</p>
                              </div>
                            )}
                            
                            {/* Display other fields that might exist */}
                            {Object.keys(traveler).map(key => {
                              // Exclude supporting document fields from this dynamic section since we handle them separately
                              if (!['id', 'application_id', 'first_name', 'last_name', 'passport_number', 'email', 'phone', 'gender', 'date_of_birth', 'dob', 'nationality', 'passport_expiry', 'passport_issue_date', 'passport_issue_country', 'created_at', 'updated_at', 'passport_type_id', 'supporting_document_id', 'supporting_doc_id', 'supporting_doc_expiry_date', 'supporting_doc_expiry_unlimited', 'place_of_birth', 'mother_name', 'father_name', 'address', 'supporting_doc_type', 'supporting_doc_number', 'supporting_doc_start_date'].includes(key) && !key.toLowerCase().includes('supporting')) {
                                return (
                                  <div key={key}>
                                    <p className="text-sm font-medium text-gray-900">
                                      {traveler[key]}
                                    </p>
                                    <p className="text-sm text-gray-500">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                                  </div>
                                );
                              }
                              return null;
                            })}
                            
                                                        {/* Supporting Document Section */}
                            {(traveler.supporting_document_id || traveler.supporting_doc_type || traveler.supporting_doc_number) && (
                              <>
                                <div className="border-t border-gray-200 pt-4 mt-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-3">Destekleyici Belge Bilgileri</h4>
                                  
                                  {/* Supporting Doc Type */}
                                  {traveler.supporting_doc_type && (
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {traveler.supporting_doc_type}
                                      </p>
                                      <p className="text-sm text-gray-500">Destekleyici Belge Türü</p>
                                    </div>
                                  )}
                                  
                                  {/* Supporting Doc Number */}
                                  {traveler.supporting_doc_number && (
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {traveler.supporting_doc_number}
                                      </p>
                                      <p className="text-sm text-gray-500">Destekleyici Belge No</p>
                                    </div>
                                  )}
                                  
                                  {/* Supporting Doc Start Date */}
                                  {traveler.supporting_doc_start_date && (
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatDate(traveler.supporting_doc_start_date)}
                                      </p>
                                      <p className="text-sm text-gray-500">Destekleyici Belge Başlangıç</p>
                                    </div>
                                  )}
                                  
                                  {/* Supporting Doc Expiry Date - only show if not unlimited */}
                                  {traveler.supporting_doc_expiry_unlimited === 0 && traveler.supporting_doc_expiry_date && (
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {formatDate(traveler.supporting_doc_expiry_date)}
                                      </p>
                                      <p className="text-sm text-gray-500">Destekleyici Belge Geçerlilik</p>
                                    </div>
                                  )}
                                  
                                  {/* Supporting Doc Expiry Unlimited Status */}
                                  {traveler.supporting_doc_expiry_unlimited !== null && traveler.supporting_doc_expiry_unlimited !== undefined && (
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">
                                        {traveler.supporting_doc_expiry_unlimited === 1 ? 'Süresiz' : 'Süreli'}
                                      </p>
                                      <p className="text-sm text-gray-500">Geçerlilik Durumu</p>
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                         </div>
                       </div>
                     ))}
                  </div>
                </div>
              </div>
            )}

                         {/* VIP Transfers */}
             {vipTransfers.length > 0 && (
               <div className="bg-white shadow rounded-lg">
                 <div className="px-4 py-5 sm:p-6">
                   <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                     VIP Transfer Bilgileri ({vipTransfers.length})
                   </h3>
                   <div className="space-y-6">
                     {vipTransfers.map((transfer, index) => (
                       <div key={index} className="border border-gray-200 rounded-lg p-6">
                                                   {/* Vehicle Info */}
                          <div className="text-center mb-6">
                            <p className="text-lg font-semibold text-gray-900">
                              {transfer.vehicle_type_name}
                            </p>
                            <p className="text-sm text-gray-500">Araç Türü</p>
                          </div>

                                                   {/* Route Information */}
                          {(transfer.pickup_location || transfer.dropoff_location) && (
                            <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-4 mb-4">
                              {/* Desktop View */}
                              <div className="hidden md:flex items-center justify-between">
                                <div className="flex-1 text-center">
                                  <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mb-2"></div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {transfer.pickup_location || 'Belirtilmemiş'}
                                  </p>
                                  <p className="text-xs text-gray-500">Alış Noktası</p>
                                </div>
                                
                                <div className="flex-1 flex items-center justify-center px-4">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-8 h-0.5 bg-gray-300"></div>
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                    </svg>
                                    <div className="w-8 h-0.5 bg-gray-300"></div>
                                  </div>
                                </div>
                                
                                <div className="flex-1 text-center">
                                  <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-2"></div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {transfer.dropoff_location || 'Belirtilmemiş'}
                                  </p>
                                  <p className="text-xs text-gray-500">Varış Noktası</p>
                                </div>
                              </div>

                              {/* Mobile View */}
                              <div className="md:hidden space-y-4">
                                <div className="text-center">
                                  <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mb-2"></div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {transfer.pickup_location || 'Belirtilmemiş'}
                                  </p>
                                  <p className="text-xs text-gray-500">Alış Noktası</p>
                                </div>
                                
                                                                 <div className="flex items-center justify-center">
                                   <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                   </svg>
                                 </div>
                                
                                <div className="text-center">
                                  <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-2"></div>
                                  <p className="text-sm font-medium text-gray-900">
                                    {transfer.dropoff_location || 'Belirtilmemiş'}
                                  </p>
                                  <p className="text-xs text-gray-500">Varış Noktası</p>
                                </div>
                              </div>
                            </div>
                          )}

                         
                       </div>
                     ))}
                   </div>
                 </div>
               </div>
             )}

            {/* Notes */}
            {application.notes && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Notlar
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {application.notes}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Financial Information */}
          <div className="space-y-6">
                         <div className="bg-white shadow rounded-lg">
               <div className="px-4 py-5 sm:p-6">
                 <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                   Finansal Bilgiler
                 </h3>
                 <div className="space-y-4">
                   {/* Order Items */}
                   {orderItems.length > 0 && (
                     <>
                       <div className="border-t border-gray-200 pt-4">
                         <div className="space-y-2">
                           {orderItems.map((item, index) => (
                             <div key={index} className="bg-gray-50 rounded-lg p-3">
                               <div className="flex items-center justify-between">
                                 <div>
                                   <p className="text-sm font-medium text-gray-900">
                                     {item.name}
                                   </p>
                                   <p className="text-xs text-gray-500">
                                     Miktar: {item.meta['_qty'] || 1} adet
                                   </p>
                                 </div>
                                 <div className="text-right">
                                   <p className="text-sm font-medium text-gray-900">
                                     ${Number(item.meta['_line_total'] || 0).toFixed(2)}
                                   </p>
                                   <p className="text-xs text-gray-500">
                                     Birim: ${Number(item.meta['_line_subtotal'] || 0).toFixed(2)}
                                   </p>
                                 </div>
                               </div>
                             </div>
                           ))}
                         </div>
                       </div>
                     </>
                   )}
                   <div className="border-t border-gray-200 pt-4">
                     <div className="flex items-center justify-between">
                       <span className="text-base font-medium text-gray-900">Toplam</span>
                       <span className="text-base font-bold text-gray-900">
                         ${Number(calculations?.totalAmount || 0).toFixed(2)}
                       </span>
                     </div>
                   </div>
                 </div>
               </div>
             </div>

                         {/* Order Information */}
             <div className="bg-white shadow rounded-lg">
               <div className="px-4 py-5 sm:p-6">
                 <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                   Sipariş Bilgileri
                 </h3>
                 <div className="space-y-3">
                   <div>
                     <p className="text-sm text-gray-500">Sipariş ID</p>
                     <p className="text-sm font-medium text-gray-900">
                       #{application.order_id || 'N/A'}
                     </p>
                   </div>
                   
                   {orderDetails && (
                     <>
                       <div>
                         <p className="text-sm text-gray-500">Sipariş Durumu</p>
                         <p className="text-sm font-medium text-gray-900">
                           {orderDetails.post_status}
                         </p>
                       </div>
                       
                       <div>
                         <p className="text-sm text-gray-500">Sipariş Tutarı</p>
                         <p className="text-sm font-medium text-gray-900">
                           ${Number(orderDetails.order_total || 0).toFixed(2)}
                         </p>
                       </div>
                       
                       <div>
                         <p className="text-sm text-gray-500">Sipariş Tarihi</p>
                         <p className="text-sm font-medium text-gray-900">
                           {formatDate(orderDetails.post_date, 'datetime')}
                         </p>
                       </div>
                     </>
                   )}
                   
                   <div>
                     <p className="text-sm text-gray-500">Oluşturulma Tarihi</p>
                     <p className="text-sm font-medium text-gray-900">
                       {formatDate(application.created_at, 'datetime')}
                     </p>
                   </div>
                 </div>
               </div>
             </div>

             
          </div>
        </div>
      </div>
  );
};

export default ApplicationDetail;
